import styles from './SidePanelFileInfo.module.css';
import formStyles from '@he-novation/design-system/styles/form-styles.module.css';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { FFProbeResult } from '@he-novation/config/types/asset.types';
import { CustomFieldValues } from '@he-novation/config/types/custom-field.types';
import { CustomFieldAssociationType } from '@he-novation/config/types/db/enums';
import { CustomFieldValuesBody } from '@he-novation/config/types/payloads/custom-field.payload';
import { acl } from '@he-novation/config/utils/acl';
import Accordion from '@he-novation/design-system/components/lists/Accordion/Accordion';
import { Error } from '@he-novation/design-system/components/text/Error/Error';
import { Loader } from '@he-novation/design-system/components/widgets/Loader/Loader';
import {
    asyncFileCustomFieldsFetch,
    asyncFileCustomFieldsUpdate
} from '@he-novation/front-shared/async/file.async';
import cn from 'classnames';
import { useAtomValue } from 'jotai';
import { debounce } from 'lodash';

import { useAssociationCustomFields } from '$atoms/custom-fields-atoms';
import { workspaceNameAtom } from '$atoms/workspace-atoms';
import { CustomFormField } from '$components/CustomFields/CustomFormField';
import { FileInfoDescription } from '$components/file/FileInfo/FileInfoDescription';
import { FileInfoGeneral } from '$components/file/FileInfo/FileInfoGeneral';
import { FileInfoMetadataAle } from '$components/file/FileInfo/FileInfoMetadataAle';
import { FileInfoMetadataCodec } from '$components/file/FileInfo/FileInfoMetadataCodec';
import { FileInfoMetadataExif } from '$components/file/FileInfo/FileInfoMetadataExif';
import { FileInfoTags } from '$components/file/FileInfo/FileInfoTags';
import { useTranslate } from '$hooks/useTranslate';
import { folderRoleSelector } from '$redux/content/folder/folderSelectors';

type SidePanelFileInfoProps = {
    uuid?: string;
    name?: string;
    version?: number;
    created?: Date | null;
    updated?: Date | null;
    description?: string | null;
    label?: string;
    mimeType?: string | null;
    size?: number;
    tags?: string[];
    exifResult?: Record<string, any>;
    ffprobeResult?: FFProbeResult;
    metadataAle?: Record<string, any>;
    fileCustomFields?: CustomFieldValuesBody;
    setFileCustomFields: (
        values: CustomFieldValues | ((prev: CustomFieldValues) => CustomFieldValues)
    ) => void;
    readonly?: boolean;
};

export function SidePanelFileInfo({
    uuid,
    version,
    name,
    mimeType,
    size,
    created,
    updated,
    tags,
    description,
    label,
    exifResult,
    ffprobeResult,
    metadataAle,
    fileCustomFields,
    setFileCustomFields,
    readonly
}: SidePanelFileInfoProps) {
    const { t } = useTranslate();

    const workspaceName = useAtomValue(workspaceNameAtom);

    const associatedCustomFields = useAssociationCustomFields(CustomFieldAssociationType.FILES);

    const { folderRole } = useSelector(folderRoleSelector);

    const updateFileCustomFields = useMemo(
        () =>
            debounce((customFields: CustomFieldValuesBody) => {
                if (!uuid) return;
                asyncFileCustomFieldsUpdate(workspaceName, uuid, customFields);
            }, 1000),
        [uuid]
    );

    if (!uuid) return <Loader />;

    if (!name || typeof version === 'undefined')
        return <Error error={t("common.Sorry, we can't find what you are looking for")} />;

    return (
        <div className={cn(styles.sidePanelFileInfo, formStyles.dark)}>
            <Accordion
                className={styles.fileInfoAccordion}
                title={t('folder.General information')}
                icon={['file', 'magnifier']}
                isOpen={true}
            >
                <FileInfoGeneral
                    name={name}
                    mimeType={mimeType}
                    size={size}
                    created={created}
                    updated={updated}
                />
            </Accordion>
            <Accordion
                className={styles.fileInfoAccordion}
                title={t('common.Description')}
                icon={'text_left'}
                isOpen={true}
            >
                <FileInfoDescription
                    uuid={uuid}
                    version={version}
                    description={description}
                    readOnly={readonly || !acl.files.setInfos(folderRole)}
                />
            </Accordion>

            {associatedCustomFields.length > 0 && (
                <Accordion
                    title={t('custom-fields.Custom fields')}
                    icon={'form'}
                    isOpen={true}
                    className={styles.fileInfoAccordion}
                >
                    {associatedCustomFields.map((field) => (
                        <CustomFormField
                            key={field.uuid}
                            customField={field}
                            value={fileCustomFields?.[field.name]}
                            readOnly={readonly || !acl.files.modify(folderRole || null)}
                            onChange={(value) => {
                                setFileCustomFields((prev) => {
                                    prev[field.name] = value;
                                    updateFileCustomFields(prev);
                                    return prev;
                                });
                            }}
                        />
                    ))}
                </Accordion>
            )}

            <Accordion
                title={t('common.Tags')}
                icon={'label'}
                isOpen={true}
                className={styles.fileInfoAccordion}
            >
                <FileInfoTags
                    uuid={uuid}
                    version={version}
                    tags={tags}
                    label={label}
                    readOnly={readonly || !acl.files.setInfos(folderRole)}
                />
            </Accordion>

            {metadataAle && (
                <Accordion
                    className={styles.fileInfoAccordion}
                    title={'Avid Log Exchange (ALE)'}
                    icon={['list', 'magnifier']}
                >
                    <FileInfoMetadataAle metadataAle={metadataAle} />
                </Accordion>
            )}

            {ffprobeResult && (
                <Accordion
                    className={styles.fileInfoAccordion}
                    title={t('folder.Codec details')}
                    icon={['pictures', 'magnifier']}
                >
                    <FileInfoMetadataCodec ffprobeResult={ffprobeResult} />
                </Accordion>
            )}

            {exifResult && (
                <Accordion
                    className={styles.fileInfoAccordion}
                    title={'EXIF Data'}
                    icon={['list', 'magnifier']}
                >
                    <FileInfoMetadataExif exifResult={exifResult} />
                </Accordion>
            )}
        </div>
    );
}
